import { z } from '@tasama/utils';
import { i18n } from '@tasama/i18n';
import type {
  FormSectionSchema,
  FormFieldSchema,
  SchemaRule,
} from '@tasama/types';
import {
  collectMissingRequirements,
  type MissingRequirement,
} from '../components/dynamic-form/missing-requirements.util';

// Flattens sections[].fields[] to a single leaf-field list. Sections marked `multiRow`
// are excluded — DynamicFormRenderer renders them via DynamicRowsTable, but this builder
// doesn't validate their row data yet (see components/dynamic-form/BE_CONTRACT_OPEN_QUESTIONS.md).
// Statically `hidden: true` fields (BFF/SNOW-injected or policy-owned, e.g. `assign_to_me`)
// are excluded too — they're never rendered, so they must never be seeded, validated, or
// submitted either. This does NOT touch rule-driven conditional hiding (resolveFieldState's
// `state.hidden`) — that's a runtime concern handled separately by DynamicFormRenderer and
// withDynamicRequiredValidation.
export function flattenFields(
  sections: FormSectionSchema[]
): FormFieldSchema[] {
  return sections.flatMap((section) =>
    section.multiRow ? [] : (section.fields ?? []).filter((f) => !f.hidden)
  );
}

const RENDERABLE_TYPES = new Set([
  'text',
  'checkbox',
  'select',
  'multiselect',
  'textarea',
  'file',
  'lookupSelect',
  'datetime',
  'date',
  'email',
  'yes_no',
  'radio',
  'url',
]);

// Applies a field's ServiceNow input constraint (TDF-1410 BUG-05) to its Zod string shape.
//
// This MUST live in the Zod schema, not in register()'s rules: the form is wired with
// `resolver: zodResolver(...)` (ActivityRequestFormContainer), and react-hook-form ignores
// register-level rules entirely whenever a resolver is present. A `pattern` rule passed to
// register() is silently dead code there.
//
// Empty string always passes: mandatory-ness is enforced separately by
// withDynamicRequiredValidation's superRefine, so requiring a value here would double-report
// an error on every optional field the user left blank.
//
// An uncompilable pattern from SNOW is skipped rather than thrown, so one bad regex in the
// catalogue cannot take down the whole form.
const translateFallback = (key: string): string => i18n.t(`portal:${key}`);

function withValidation(
  base: z.ZodString,
  field: FormFieldSchema,
  t?: (_key: string) => string
): z.ZodTypeAny {
  const pattern = field.validation?.pattern;
  if (!pattern) return base;
  let re: RegExp;
  try {
    re = new RegExp(pattern);
  } catch {
    return base;
  }
  // `t` is optional because this builder is also called just to read zod's defaults, where no
  // message is ever surfaced. The fallback goes through the i18n singleton rather than an English
  // literal, so the message still follows the active locale. (TDF-1410 FE-3)
  const message =
    field.validation?.message ??
    (t ?? translateFallback)(
      'services.activityRequest.validation.invalidValue'
    );
  return base.refine((v) => v === '' || re.test(v), { message });
}

// Builds the z.object() shape for a CatalogItemSchema's sections, following the existing
// get<Feature>Schema(t, ...) factory convention (sow.schema.ts, activity-request.schema.ts).
// Kept as a plain ZodObject (not `.superRefine`'d yet) so callers can `.merge()` it
// with another object schema before layering conditional-required validation on top.
export function buildDynamicFormSchema(
  sections: FormSectionSchema[],
  t?: (_key: string) => string
) {
  const fields = flattenFields(sections).filter((f) =>
    RENDERABLE_TYPES.has(f.type)
  );

  const shape: Record<string, z.ZodTypeAny> = {};

  // A multi-row set is a section, not a field, so flattenFields skips it - but its rows still belong
  // in the form value, otherwise zod strips the key and neither the required check nor the submit
  // payload can see the grid at all.
  sections
    .filter((section) => section.multiRow && section.key)
    .forEach((section) => {
      shape[section.key] = z
        .array(z.record(z.string(), z.unknown()))
        .optional()
        .default([]);
    });

  fields.forEach((field) => {
    if (field.type === 'checkbox') {
      shape[field.name] = z.boolean().default(false);
      return;
    }
    if (field.type === 'file') {
      shape[field.name] = z.array(z.instanceof(File)).optional().default([]);
      return;
    }
    if (field.type === 'email') {
      // Empty string allowed here — mandatory-ness is enforced separately by
      // withDynamicRequiredValidation's superRefine, not this shape.
      shape[field.name] = z
        .union([z.literal(''), z.string().email()])
        .optional()
        .default('');
      return;
    }
    if (field.type === 'multiselect') {
      // MultiSelectDropdown emits string[] (see its onChange signature), so the shape has to be an
      // array. It used to fall through to the z.string() default below: the parse then failed for any
      // non-empty selection, react-hook-form went invalid, and SUBMIT disabled itself with no error
      // rendered anywhere - picking a single option made the form permanently unsubmittable, and a
      // mandatory multiselect could never be submitted at all.
      shape[field.name] = z.array(z.string()).optional().default([]);
      return;
    }
    if (field.type === 'url') {
      shape[field.name] = z
        .union([z.literal(''), z.string().url()])
        .optional()
        .default('');
      return;
    }
    shape[field.name] = withValidation(z.string(), field, t)
      .optional()
      .default('');
  });

  return z.object(shape);
}

// Every requirement the dynamic part of the form has not met yet, evaluated against live values.
//
// This is the single source of truth behind both halves of the feature: the superRefine below
// turns it into the Zod issues that keep SUBMIT disabled, and RequiredInformationSummary renders
// it as the panel naming the blockers. Sharing one pass is what guarantees the button and the
// explanation can never disagree (TDF-1410).
export function getMissingRequirements(
  sections: FormSectionSchema[],
  rules: SchemaRule[],
  values: Record<string, unknown>,
  t: (_key: string) => string
): MissingRequirement[] {
  const fields = flattenFields(sections).filter((f) =>
    RENDERABLE_TYPES.has(f.type)
  );
  return collectMissingRequirements(sections, fields, rules, values, t);
}

// Re-checks the top-level staticRules[] (REQUIRE effect) plus the group rules (§3
// REQUIRE_ANY_OF, §4 VALIDATE) against the submitted values — the FE-side counterpart of the
// contract's "BE re-validates the same rule at submit" principle. Apply after merging the
// dynamic shape into the full form schema.
export function withDynamicRequiredValidation<T extends z.ZodTypeAny>(
  schema: T,
  sections: FormSectionSchema[],
  rules: SchemaRule[],
  t: (_key: string) => string
) {
  return schema.superRefine((values, ctx) => {
    getMissingRequirements(
      sections,
      rules,
      values as Record<string, unknown>,
      t
    ).forEach(({ paths, message }) => {
      paths.forEach((path) => {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [path],
          message,
        });
      });
    });
  });
}
